import React, { useState, useEffect, createRef, cloneElement } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Popping from 'poppingjs';
import { PoppingFlip } from 'poppingjs/modifiers';
import { PoppingResize } from 'poppingjs/plugins';
import { css } from 'Common/Utils';
import style from './index.module.css';

const container = document.createElement('div');

const TooltipProps = {
	variant: PropTypes.oneOf(['dark']),
	persistent: PropTypes.bool,
	message: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
		.isRequired,
	placement: PropTypes.oneOf([
		'top',
		'top-left',
		'top-right',
		'bottom',
		'bottom-left',
		'bottom-right',
		'left',
		'left-top',
		'left-bottom',
		'right',
		'right-top',
		'right-bottom',
	]),
	offset: PropTypes.arrayOf(PropTypes.number),
	allowOnClick: PropTypes.bool,
	style: PropTypes.object,
	children: PropTypes.element.isRequired,
};

const TooltipDefault = {
	persistent: false,
	placement: 'top-right',
	offset: [10, 10],
	allowOnClick: false,
	style: {},
};

const Tooltip = ({
	variant,
	persistent,
	message,
	placement,
	offset,
	children,
	allowOnClick,
	style: popStyle,
}) => {
	const [state, setState] = useState({ show: false, transition: false });
	const isClose = !(state.show || state.transition);
	const targetRef = createRef();
	const popRef = createRef();
	const isReferable = typeof children.type === 'string';

	const handleEnter = () => {
		clearTimeout(state.timeout);
		setState({
			show: true,
			transition: false,
			timeout: setTimeout(() => setState({ show: true, transition: true }), 50),
		});
	};
	const handleLeave = () => {
		clearTimeout(state.timeout);
		setState({
			show: true,
			transition: false,
			timeout: setTimeout(
				() => setState({ show: false, transition: false }),
				200
			),
		});
	};
	let props = {
		ref: targetRef,
		'data-tooltip': true,
		...(persistent ||
			(allowOnClick
				? {
						onClick: () => {
							const status = state.show && state.transition;
							if (!status) {
								handleEnter();
							}
							if (status) {
								handleLeave();
							}
						},
				  }
				: {
						onMouseEnter: handleEnter,
						onMouseLeave: handleLeave,
				  })),
	};

	useEffect(() => {
		const target = targetRef.current;
		const pop = popRef.current;
		if (!(target && pop)) return;

		const { display } = isReferable
			? {}
			: window.getComputedStyle(target.children[0]);
		css(target, {
			display,
		});
		const PoppingScroll = (instance) => {
			const scroll = () => {
				instance.updatePosition();
			};

			window.addEventListener('scroll', scroll, false);

			return {
				destroy() {
					window.removeEventListener('scroll', scroll, false);
				},
			};
		};
		const cleanup = Popping.create(target, pop, {
			placement,
			offset,
			rotateArrow: 45,
			allowRootRelative: true,
			modifiers: [PoppingFlip],
			plugins: [PoppingResize, PoppingScroll],
		});
		return () => cleanup.destroy();
	}, [popRef, targetRef, placement, offset, persistent, isClose, isReferable]);

	return (
		<>
			{isReferable ? (
				cloneElement(children, {
					...props,
				})
			) : (
				<div className={style.overlay} {...props}>
					{children}
				</div>
			)}
			{(state.show || persistent) &&
				ReactDOM.createPortal(
					<div
						ref={popRef}
						role='dialog'
						aria-hidden='false'
						className={`${style.tooltip} ${
							state.transition || persistent ? style.show : ''
						} ${variant === 'dark' ? style.dark : ''}`}
						style={popStyle}>
						{message}
					</div>,
					container
				)}
		</>
	);
};

Tooltip.propTypes = TooltipProps;
Tooltip.defaultProps = TooltipDefault;

document.body.appendChild(container);

export default Tooltip;